import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Topic, SessionSettings, Team } from './types/index.ts';
import { topicApi } from './services/api.ts';
import { authService } from './services/auth.ts';
import { useSeriesFromURL } from './hooks/useSeriesFromURL.ts';
import TopicTable from './components/TopicTable.tsx';
import TopicSubmissionModal from './components/TopicSubmissionModal.tsx';
import SessionSettingsModal from './components/SessionSettingsModal.tsx';
import TeamSelectionModal from './components/TeamSelectionModal.tsx';
import EditTeamModal from './components/EditTeamModal.tsx';
import PastTopicsPage from './components/PastTopicsPage.tsx';
import MyTopicStatsPage from './components/MyTopicStatsPage.tsx';
import DeletedTopicsPage from './components/DeletedTopicsPage.tsx';
import AboutPage from './components/AboutPage.tsx';
import SeriesInfoModal from './components/SeriesInfoModal.tsx';
import SeriesInfoEditPage from './components/SeriesInfoEditPage.tsx';
import NavigationMenu from './components/NavigationMenu.tsx';
import FeedbackPage from './components/FeedbackPage.tsx';
import './App.css';

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [nextTopics, setNextTopics] = useState<Topic[]>([]);
  const [futureTopics, setFutureTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showCreateTeamDirectly, setShowCreateTeamDirectly] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [sessionSettings, setSessionSettings] = useState<SessionSettings | null>(null);
  const [autoOpenFeedbackTopicId, setAutoOpenFeedbackTopicId] = useState<string | null>(null);

  const [showSeriesInfoModal, setShowSeriesInfoModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showFeedbackPage, setShowFeedbackPage] = useState<{topicId: string, teamId: string} | null>(null);

  // Use URL hook for series validation
  const { validatedTeam, shouldClearTeam } = useSeriesFromURL(availableTeams);
  const [clearTeamProcessed, setClearTeamProcessed] = useState(false);

  // Determine which page to show based on URL
  const currentPage = React.useMemo(() => {
    const path = location.pathname;
    if (path.includes('/past-topics')) return 'past-topics';
    if (path.includes('/deleted-topics')) return 'deleted-topics';
    if (path.includes('/my-stats')) return 'my-stats';
    if (path.includes('/about')) return 'about';
    if (path.includes('/series-info/edit')) return 'series-info-edit';
    return 'scheduled-topics'; // default
  }, [location.pathname]);

  // Sync URL-validated team with currentTeam state
  useEffect(() => {
    if (validatedTeam && (!currentTeam || currentTeam.teamId !== validatedTeam.teamId)) {
      setCurrentTeam(validatedTeam);
      loadData(validatedTeam.teamId);
      loadSessionSettings(validatedTeam.teamId);
      setClearTeamProcessed(false); // Reset flag when loading valid team
    }
  }, [validatedTeam?.teamId]); // Trigger on teamId changes

  // Clear team when invalid teamId detected
  useEffect(() => {
    if (shouldClearTeam && !clearTeamProcessed) {
      setCurrentTeam(null);
      setNextTopics([]);
      setFutureTopics([]);
      setClearTeamProcessed(true); // Mark as processed
    }
  }, [shouldClearTeam, clearTeamProcessed]);

  useEffect(() => {
    handleAuthentication();
  }, []);

  const handleAuthentication = async () => {
    console.log('handleAuthentication called');
    // Check if we're returning from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('code')) {
      console.log('OAuth callback detected');
      const success = await authService.handleCallback();
      if (success) {
        // Check if we have a saved URL to redirect to
        const savedUrl = localStorage.getItem('pendingUrlAfterAuth');
        if (savedUrl) {
          localStorage.removeItem('pendingUrlAfterAuth');
          navigate(savedUrl);
        }
        initializeApp();
      } else {
        setError('Authentication failed. Please try again.');
        setLoading(false);
        // Redirect to login after a delay
        setTimeout(() => {
          authService.redirectToLogin();
        }, 2000);
      }
      return;
    }

    // Check if user is already authenticated
    if (authService.isAuthenticated()) {
      console.log('User already authenticated, calling initializeApp');
      initializeApp();
    } else {
      console.log('User not authenticated, redirecting to login');
      
      // Save current URL path before redirecting to auth
      const currentPath = location.pathname;
      if (currentPath && currentPath !== '/') {
        localStorage.setItem('pendingUrlAfterAuth', currentPath);
      }
      
      // Save feedback URL parameters before redirecting to auth
      const feedbackTopicId = urlParams.get('feedback');
      const feedbackTeamId = urlParams.get('team');
      if (feedbackTopicId && feedbackTeamId) {
        localStorage.setItem('pendingFeedbackAfterAuth', JSON.stringify({
          topicId: feedbackTopicId,
          teamId: feedbackTeamId
        }));
      }
      
      // Redirect to login
      authService.redirectToLogin();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTeamDropdown) {
        setShowTeamDropdown(false);
      }
      if (showUserDropdown) {
        setShowUserDropdown(false);
      }
    };

    if (showTeamDropdown || showUserDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTeamDropdown, showUserDropdown]);

  const initializeApp = async () => {
    console.log('initializeApp called');
    try {
      setError(''); // Clear any previous errors
      
      // Always load teams from database first
      const teams = await topicApi.getTeams();
      console.log('Teams loaded:', teams.length);
      
      // Set availableTeams so URL hook can validate teamId
      setAvailableTeams(teams);
      
      if (teams.length === 0) {
        // No teams exist, show team creation modal
        setShowTeamModal(true);
        setLoading(false);
        return;
      }

      // Check for saved feedback parameters after authentication
      const savedFeedbackParams = localStorage.getItem('pendingFeedbackAfterAuth');
      if (savedFeedbackParams) {
        console.log('Found saved feedback parameters after auth');
        const { topicId, teamId } = JSON.parse(savedFeedbackParams);
        
        // Find the team
        const targetTeam = teams.find(team => team.teamId === teamId);
        if (targetTeam) {
          // Navigate to feedback page
          setShowFeedbackPage({ topicId, teamId });
          setCurrentTeam(targetTeam);
          localStorage.setItem('selectedTeamId', targetTeam.teamId);
          
          // Clean up saved parameters
          localStorage.removeItem('pendingFeedbackAfterAuth');
          setLoading(false);
          return;
        } else {
          setError('Team not found for feedback link.');
          localStorage.removeItem('pendingFeedbackAfterAuth');
        }
      }

      // Check for feedback URL parameters first
      console.log('Checking for feedback URL parameters');
      const feedbackTeamFromUrl = await handleFeedbackUrl(teams);
      
      // Continue with normal initialization, but use team from URL if provided
      let selectedTeam = null;
      
      if (feedbackTeamFromUrl) {
        console.log('Using team from feedback URL:', feedbackTeamFromUrl.teamName);
        selectedTeam = feedbackTeamFromUrl;
      } else if (location.pathname !== '/') {
        // Only load from localStorage if NOT at root path
        // Root path should show welcome message
        const savedTeamId = localStorage.getItem('selectedTeamId');
        
        if (savedTeamId) {
          // Verify the saved team still exists in database
          const savedTeam = teams.find(team => team.teamId === savedTeamId);
          if (savedTeam) {
            selectedTeam = savedTeam;
          }
        }
      }

      if (selectedTeam) {
        setCurrentTeam(selectedTeam);
        loadData(selectedTeam.teamId);
        loadSessionSettings(selectedTeam.teamId);
        setLoading(false);
        return;
      }

      // No valid team, load app without team selection
      setLoading(false);
    } catch (error) {
      console.error('Failed to initialize app:', error);
      setError('Failed to load teams. Make sure the backend server is running.');
      setShowTeamModal(true); // Still show team modal even on error
      setLoading(false);
    }
  };

  const handleFeedbackUrl = async (teams: Team[]): Promise<Team | null> => {
    const urlParams = new URLSearchParams(window.location.search);
    const feedbackTopicId = urlParams.get('feedback');
    const feedbackTeamId = urlParams.get('team');

    if (feedbackTopicId && feedbackTeamId) {
      try {
        // Find the team
        const targetTeam = teams.find(team => team.teamId === feedbackTeamId);
        if (!targetTeam) {
          setError('Team not found for feedback link.');
          return null;
        }

        // Navigate to feedback page instead of modal
        setShowFeedbackPage({ topicId: feedbackTopicId, teamId: feedbackTeamId });

        // Clean up URL parameters
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);

        return targetTeam;
      } catch (error) {
        console.error('Error handling feedback URL:', error);
        setError('Topic no longer exists or could not be loaded.');
        return null;
      }
    }
    return null;
  };

  const handleFeedbackComplete = () => {
    setShowFeedbackPage(null);
    // Navigate to series URL after feedback
    if (currentTeam) {
      navigate(`/series/${currentTeam.teamId}/scheduled-topics`);
      loadData(currentTeam.teamId);
    } else {
      navigate('/scheduled-topics');
    }
  };

  const handleTeamSelected = (team: Team) => {
    setCurrentTeam(team);
    localStorage.setItem('selectedTeamId', team.teamId);
    setShowTeamModal(false);
    setShowCreateTeamDirectly(false); // Reset the flag
    loadData(team.teamId);
    loadSessionSettings(team.teamId);
  };

  const handleTeamDropdownToggle = async () => {
    if (!showTeamDropdown) {
      // Load teams when opening dropdown
      setLoadingTeams(true);
      try {
        const teams = await topicApi.getTeams();
        setAvailableTeams(teams);
      } catch (error) {
        console.error('Failed to load teams:', error);
      } finally {
        setLoadingTeams(false);
      }
    }
    setShowTeamDropdown(!showTeamDropdown);
  };

  const handleTeamSelect = (team: Team) => {
    // Prevent duplicate calls - if we're already on this team, ignore
    if (currentTeam && currentTeam.teamId === team.teamId) {
      setShowTeamDropdown(false);
      return;
    }
    
    setCurrentTeam(team);
    localStorage.setItem('selectedTeamId', team.teamId);
    setShowTeamDropdown(false);
    
    // Update URL to include teamId
    const currentPath = location.pathname;
    if (currentPath.includes('/past-topics')) {
      navigate(`/series/${team.teamId}/past-topics`);
    } else if (currentPath.includes('/deleted-topics')) {
      navigate(`/series/${team.teamId}/deleted-topics`);
    } else if (currentPath.includes('/my-stats')) {
      navigate(`/series/${team.teamId}/my-stats`);
    } else {
      navigate(`/series/${team.teamId}/scheduled-topics`);
    }
    
    loadData(team.teamId);
    loadSessionSettings(team.teamId);
  };

  const handleEditTeam = () => {
    setShowEditTeamModal(true);
  };

  const handleTeamUpdated = (updatedTeam: Team) => {
    setCurrentTeam(updatedTeam);
    // Update localStorage with new team name (though we only store ID)
    localStorage.setItem('selectedTeamId', updatedTeam.teamId);
  };

  const handleTeamDeleted = async () => {
    // Clear localStorage
    localStorage.removeItem('selectedTeamId');
    // Close the modal
    setShowEditTeamModal(false);
    // Clear current team
    setCurrentTeam(null);
    // Refresh teams list
    try {
      const teams = await topicApi.getTeams();
      setAvailableTeams(teams);
    } catch (err) {
      console.error('Error refreshing teams after deletion:', err);
    }
    // Navigate to welcome screen
    navigate('/');
  };

  const loadSessionSettings = async (teamId: string) => {
    try {
      const settings = await topicApi.getSessionSettings(teamId);
      setSessionSettings(settings);
    } catch (error) {
      console.error('Failed to load session settings:', error);
    }
  };

  const saveSessionSettings = async (settings: SessionSettings) => {
    if (!currentTeam) return;
    
    try {
      await topicApi.saveSessionSettings(settings, currentTeam.teamId);
      setSessionSettings(settings);
    } catch (error) {
      console.error('Failed to save session settings:', error);
    }
  };



  const loadData = async (teamId: string) => {
    setLoading(true);
    setError('');
    
    try {
      const { next, future } = await topicApi.getTopicsBySession(teamId);
      setNextTopics(next);
      setFutureTopics(future);
      
    } catch (error: any) {
      setError('Failed to load data. Make sure the backend server is running.');
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDataUpdate = (submittedTeamId?: string) => {
    // If a teamId was provided (from topic submission), switch to that team
    if (submittedTeamId && availableTeams.length > 0) {
      const submittedTeam = availableTeams.find(t => t.teamId === submittedTeamId);
      if (submittedTeam && (!currentTeam || currentTeam.teamId !== submittedTeamId)) {
        setCurrentTeam(submittedTeam);
        localStorage.setItem('selectedTeamId', submittedTeamId);
        loadData(submittedTeamId);
        loadSessionSettings(submittedTeamId);
        navigate(`/series/${submittedTeamId}/scheduled-topics`);
        setAutoOpenFeedbackTopicId(null);
        return;
      }
    }
    
    // Otherwise, just reload current team's data
    if (currentTeam) {
      loadData(currentTeam.teamId);
      // Navigate to scheduled-topics after topic submission
      navigate(`/series/${currentTeam.teamId}/scheduled-topics`);
    } else {
      // No current team - just clear the error
      setError('');
    }
    // Clear auto-open feedback after any update
    setAutoOpenFeedbackTopicId(null);
  };

  const clearAutoOpenFeedback = () => {
    setAutoOpenFeedbackTopicId(null);
  };

  const formatSessionDisplay = () => {
    if (!sessionSettings?.sessionDate || !sessionSettings?.sessionTime) {
      return 'TBD';
    }
    
    const [year, month, day] = sessionSettings.sessionDate.split('-').map(Number);
    const [hours, minutes] = sessionSettings.sessionTime.split(':').map(Number);
    const utcDateTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
    
    const dateStr = utcDateTime.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    
    const timeStr = utcDateTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeZoneName: 'short'
    });
    
    return `${dateStr} at ${timeStr}`;
  };

  if (showTeamModal) {
    return <TeamSelectionModal 
      onTeamSelected={handleTeamSelected} 
      onClose={() => setShowTeamModal(false)}
      showCreateFormDirectly={showCreateTeamDirectly}
    />;
  }

  if (loading) {
    return (
      <div className="app">
        <div className="loading">Loading ShareBrief sessions...</div>
      </div>
    );
  }

  const handleNavigation = (page: 'main' | 'past-topics' | 'my-topic-stats' | 'deleted-topics' | 'submit-topic' | 'about') => {
    if (page === 'submit-topic') {
      setShowSubmissionModal(true);
    }
    // Other pages are handled by NavigationMenu's navigate() calls
  };

  if (showFeedbackPage) {
    return (
      <FeedbackPage 
        topicId={showFeedbackPage.topicId}
        teamId={showFeedbackPage.teamId}
        onComplete={handleFeedbackComplete}
      />
    );
  }

  if (currentPage === 'past-topics') {
    return (
      <>
        <PastTopicsPage 
          currentTeam={currentTeam}
          onBackToMain={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/scheduled-topics` : '/')}
          onNavigateToMyTopicStats={() => navigate('/my-stats')}
          onNavigateToDeletedTopics={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/deleted-topics` : '/')}
          onNavigateToAbout={() => navigate('/about')}
          onSubmitNewTopic={() => setShowSubmissionModal(true)}
        />
        {showSubmissionModal && (
          <TopicSubmissionModal
            onClose={() => setShowSubmissionModal(false)}
            onTopicSubmitted={handleDataUpdate}
            sessionSettings={sessionSettings}
            currentTeam={currentTeam}
          />
        )}
      </>
    );
  }

  if (currentPage === 'my-stats') {
    return (
      <>
        <MyTopicStatsPage 
          onBackToMain={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/scheduled-topics` : '/')}
          onNavigateToPastTopics={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/past-topics` : '/')}
          onNavigateToDeletedTopics={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/deleted-topics` : '/')}
          onNavigateToAbout={() => navigate('/about')}
          onSubmitNewTopic={() => setShowSubmissionModal(true)}
        />
        {showSubmissionModal && (
          <TopicSubmissionModal
            onClose={() => setShowSubmissionModal(false)}
            onTopicSubmitted={handleDataUpdate}
            sessionSettings={sessionSettings}
            currentTeam={currentTeam}
          />
        )}
      </>
    );
  }

  if (currentPage === 'deleted-topics') {
    return (
      <>
        <DeletedTopicsPage 
          currentTeam={currentTeam}
          teams={availableTeams}
          onBack={() => {
            navigate(currentTeam ? `/series/${currentTeam.teamId}/scheduled-topics` : '/');
            handleDataUpdate(); // Refresh topics data
          }}
          onNavigateToPastTopics={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/past-topics` : '/')}
          onNavigateToMyTopicStats={() => navigate('/my-stats')}
          onNavigateToAbout={() => navigate('/about')}
          onSubmitNewTopic={() => setShowSubmissionModal(true)}
        />
        {showSubmissionModal && (
          <TopicSubmissionModal
            onClose={() => setShowSubmissionModal(false)}
            onTopicSubmitted={handleDataUpdate}
            sessionSettings={sessionSettings}
            currentTeam={currentTeam}
          />
        )}
      </>
    );
  }

  if (currentPage === 'about') {
    return (
      <>
        <AboutPage 
          currentTeam={currentTeam}
          onBack={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/scheduled-topics` : '/')}
          onNavigateToPastTopics={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/past-topics` : '/')}
          onNavigateToMyTopicStats={() => navigate('/my-stats')}
          onNavigateToDeletedTopics={() => navigate(currentTeam ? `/series/${currentTeam.teamId}/deleted-topics` : '/')}
          onSubmitNewTopic={() => setShowSubmissionModal(true)}
        />
        {showSubmissionModal && (
          <TopicSubmissionModal
            onClose={() => setShowSubmissionModal(false)}
            onTopicSubmitted={handleDataUpdate}
            sessionSettings={sessionSettings}
            currentTeam={currentTeam}
          />
        )}
      </>
    );
  }

  if (currentPage === 'series-info-edit' && currentTeam) {
    return (
      <SeriesInfoEditPage 
        teamId={currentTeam.teamId}
        teamName={currentTeam.teamName}
        onBack={() => navigate(`/series/${currentTeam.teamId}/scheduled-topics`)}
        onNavigate={handleNavigation}
        onShowSeriesInfoModal={() => setShowSeriesInfoModal(true)}
      />
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div>
          <h1 style={{ margin: 0 }}>ShareBrief</h1>
          <div style={{ display: 'flex', alignItems: 'center', position: 'relative', marginTop: '8px' }}>
            <h2 
              onClick={handleTeamDropdownToggle}
              style={{ 
                margin: 0, 
                color: '#2563eb', 
                fontWeight: '600',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
            >
              {currentTeam ? currentTeam.teamName : 'Select series'}
            </h2>
            <button
              onClick={handleTeamDropdownToggle}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#666',
                padding: '2px'
              }}
            >
              {loadingTeams ? (
                <span className="loading-spinner">⟳</span>
              ) : (
                '▼'
              )}
            </button>
            {currentTeam && (
              <button 
                onClick={handleEditTeam}
                style={{ 
                  marginLeft: '8px',
                  padding: '4px 8px', 
                  fontSize: '12px',
                  background: 'none',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                Edit Series
              </button>
            )}
            
            {showTeamDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '300px',
                marginTop: '4px'
              }}>
                <button
                  onClick={() => {
                    setShowTeamDropdown(false);
                    setShowCreateTeamDirectly(true);
                    setShowTeamModal(true);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    color: '#666'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  + Create New Series
                </button>
                {availableTeams.length > 0 && (
                  <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #eee' }} />
                )}
                {availableTeams.filter(team => !currentTeam || team.teamId !== currentTeam.teamId).map((team) => (
                  <button
                    key={team.teamId}
                    onClick={() => handleTeamSelect(team)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {team.teamName}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Series Information Link - Below team name on its own line */}
          {currentTeam && (
            <div style={{ marginTop: '8px' }}>
              <button
                onClick={() => setShowSeriesInfoModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  cursor: 'pointer',
                  fontSize: '16px',
                  textDecoration: 'none',
                  padding: '0'
                }}
              >
                Series Information
              </button>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {(() => {
            const userInfo = authService.getUserInfo();
            return userInfo ? (
              <div style={{ position: 'relative' }}>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUserDropdown(!showUserDropdown);
                  }}
                  style={{ 
                    fontSize: '16px', 
                    marginBottom: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Welcome <span style={{ color: '#2563eb', fontWeight: '600' }}>{userInfo.name}</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                </div>
                
                {showUserDropdown && (
                  <div 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: '0',
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 9999,
                      minWidth: '120px'
                    }}
                  >
                    <button
                      onClick={() => {
                        authService.logout();
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </div>
      </header>

      <NavigationMenu 
        currentPage="main" 
        currentTeamId={currentTeam?.teamId}
        onNavigate={handleNavigation}
      />

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={handleDataUpdate}>Retry</button>
        </div>
      )}

      <main className="app-main">
        {!currentTeam ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: '600',
              color: '#2563eb',
              margin: '0 0 32px 0'
            }}>
              Welcome to ShareBrief
            </h2>
            <p style={{
              fontSize: '1.25rem',
              color: '#333',
              margin: '0'
            }}>
              Select a series above
            </p>
          </div>
        ) : (
          <>
            {/* Next Session */}
            <section className="next-session">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h2>Next Session - {formatSessionDisplay()}{(() => {
                    const totalDuration = nextTopics.reduce((sum, topic) => sum + (topic.duration || 0), 0);
                    return totalDuration > 0 ? ` (Total: ${totalDuration} min)` : '';
                  })()}</h2>
                  <button 
                    className="settings-button"
                    onClick={() => setShowSettingsModal(true)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      background: '#f8f9fa',
                      color: '#333',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    Change Session Date
                  </button>
                </div>
              </div>
              {nextTopics.length === 0 ? (
                <div className="no-data">No topics selected for next session</div>
              ) : (
                <TopicTable
                  topics={nextTopics}
                  onTopicUpdate={handleDataUpdate}
              sessionSettings={sessionSettings}
              showAISummary={true}
              showDuration={true}
              totalDuration={nextTopics.reduce((sum, topic) => sum + (topic.duration || 0), 0)}
              autoOpenFeedbackTopicId={autoOpenFeedbackTopicId}
              onAutoOpenFeedbackCleared={clearAutoOpenFeedback}
            />
          )}
        </section>

        {/* Future Topics */}
        <section className="future-topics">
          <h2>Future Topics</h2>
          {futureTopics.length === 0 ? (
            <div className="no-data">No future topics submitted</div>
          ) : (
            <TopicTable
              topics={futureTopics}
              onTopicUpdate={handleDataUpdate}
              sessionSettings={sessionSettings}
              showDuration={true}
              showEstimatedDate={true}
              totalDuration={futureTopics.reduce((sum, topic) => sum + (topic.duration || 0), 0)}
              autoOpenFeedbackTopicId={autoOpenFeedbackTopicId}
              onAutoOpenFeedbackCleared={clearAutoOpenFeedback}
            />
          )}
        </section>

        {/* Past Topics */}
        <section className="past-sessions">
        </section>
        </>
        )}
      </main>

      {showSubmissionModal && (
        <TopicSubmissionModal
          onClose={() => setShowSubmissionModal(false)}
          onTopicSubmitted={handleDataUpdate}
          sessionSettings={sessionSettings}
          currentTeam={currentTeam}
        />
      )}

      {showSettingsModal && (
        <SessionSettingsModal
          currentSettings={sessionSettings || undefined}
          onClose={() => setShowSettingsModal(false)}
          onSave={saveSessionSettings}
        />
      )}

      {showEditTeamModal && currentTeam && (
        <EditTeamModal
          team={currentTeam}
          onClose={() => setShowEditTeamModal(false)}
          onTeamUpdated={handleTeamUpdated}
          onTeamDeleted={handleTeamDeleted}
        />
      )}

      {showSeriesInfoModal && currentTeam && (
        <SeriesInfoModal
          isOpen={showSeriesInfoModal}
          onClose={() => setShowSeriesInfoModal(false)}
          teamId={currentTeam.teamId}
          teamName={currentTeam.teamName}
          onEditClick={() => {
            setShowSeriesInfoModal(false);
            navigate(`/series/${currentTeam.teamId}/series-info/edit`);
          }}
        />
      )}
    </div>
  );
};

export default App;
